import * as api from "../../api/products";
import type { AdminCustomizationConfig, CustomOptionGroup, CustomOptionPreset, CustomOptionValue } from "../../api/products";
import { cloneGroups } from "./model";
import { isSharedStickerMaterial } from "../../views/sticker-material";
import { supportsDimensions } from "./dimensions";

export interface MappingIssue { kind: "group" | "value"; source: string; index: number }
const record = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
// MySQL JSON normalizes object key order; array order and values remain significant.
function canonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJson);
  const object = record(value);
  return object ? Object.fromEntries(Object.keys(object).sort().map(key => [key, canonicalJson(object[key])])) : value;
}
const sameJson = (a: unknown, b: unknown) => JSON.stringify(canonicalJson(a)) === JSON.stringify(canonicalJson(b));
const text = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
const id = (value: unknown): string => typeof value === "string" || typeof value === "number" ? String(value) : "";

// Filtering is a view operation. It NEVER cleans/deletes database records.
export function mapGroups(input: unknown, source: string, flatValues: unknown = []) {
  const issues: MappingIssue[] = [], groups: CustomOptionGroup[] = [];
  const rows = Array.isArray(input) ? input : [];
  if (!Array.isArray(input)) issues.push({ kind: "group", source, index: -1 });
  const seen = new Set<string>();
  rows.forEach((raw, index) => {
    const group = record(raw), groupId = id(group?.id);
    if (!group || !groupId || !text(group.code) || !text(group.name) || seen.has(group.code)
      || !["SINGLE_SELECT", "MULTI_SELECT", "NUMBER", "TEXT"].includes(String(group.inputType))) {
      issues.push({ kind: "group", source, index }); return;
    }
    seen.add(group.code);
    const values: CustomOptionValue[] = [], valueIds = new Set<string>();
    const rawValues = Array.isArray(group.values) ? group.values : Array.isArray(flatValues) ? flatValues.filter(value => id(record(value)?.groupId) === groupId) : [];
    rawValues.forEach((item, valueIndex) => {
      const value = record(item), valueId = id(value?.id);
      if (!value || !valueId || !text(value.name) || !text(value.code) || valueIds.has(valueId)) {
        issues.push({ kind: "value", source: `${source}/${groupId}`, index: valueIndex }); return;
      }
      valueIds.add(valueId);
      values.push({ ...value, id: valueId, groupId, imageUrl: typeof value.imageUrl === "string" && value.imageUrl.trim() ? value.imageUrl : null,
        sortOrder: typeof value.sortOrder === "number" && Number.isFinite(value.sortOrder) ? value.sortOrder : 0,
        isActive: value.isActive !== false, extraConfig: value.extraConfig ?? null } as CustomOptionValue);
    });
    groups.push({ ...group, id: groupId, productId: id(group.productId), values,
      sortOrder: typeof group.sortOrder === "number" && Number.isFinite(group.sortOrder) ? group.sortOrder : 0,
      isActive: group.isActive !== false, required: group.required === true, unit: typeof group.unit === "string" ? group.unit : null } as CustomOptionGroup);
  });
  return { groups, issues, totalGroups: rows.length, totalValues: rows.reduce((sum, row) => sum + (Array.isArray(record(row)?.values) ? (record(row)!.values as unknown[]).length : 0), 0) };
}

export function mapPresets(input: unknown) {
  const mapped = mapGroups(input, "public");
  const presets = mapped.groups.map(group => ({ ...group, config: group.config ?? null,
    values: group.values.map(value => ({ ...value, presetId: group.id })) })) as CustomOptionPreset[];
  return { ...mapped, presets };
}

export type ParameterApi = Pick<typeof api, "getAdminCustomizationConfig" | "createCustomOptionGroup" | "updateCustomOptionGroup" |
  "setCustomOptionGroupStatus" | "createCustomOptionValue" | "updateCustomOptionValue" | "setCustomOptionValueStatus" | "uploadCustomizationOptionImage">;
export interface ImageDraft { groupId: string; valueId: string; file: File }
export class ParameterSaveError extends Error {
  constructor(message: string, readonly attempted: boolean, readonly completed: number) { super(message); }
}

// Existing endpoints only; no batch transaction is claimed. Stop on the first error.
export async function saveParameterDraft(productId: string, baseline: CustomOptionGroup[], draft: CustomOptionGroup[], images: ImageDraft[], adapter: ParameterApi = api): Promise<AdminCustomizationConfig> {
  let attempted = false, completed = 0;
  async function write<T>(operation: () => Promise<T>): Promise<T> {
    attempted = true; const result = await operation(); completed++; return result;
  }
  try {
    const fresh = await adapter.getAdminCustomizationConfig(productId), mapped = mapGroups(fresh.optionGroups, "product", fresh.optionValues);
    if (mapped.issues.length || JSON.stringify(mapped.groups) !== JSON.stringify(baseline)) throw new Error("商品参数已被其他操作修改，请重新读取后核对");
    // Validate all new metadata before the first side effect.
    for (const group of draft) {
      if (!baseline.some(old => old.code === group.code) && group.config != null && !record(group.config)) throw new Error("参数配置格式无法无损复制，请使用原兼容配置入口");
      const original = baseline.find(old => old.code === group.code);
      if (original && supportsDimensions(group) && !sameJson(original.config, group.config)) {
        const before = { ...record(original.config) }, after = { ...record(group.config) };
        delete before.dimensionFields; delete after.dimensionFields;
        const fields = record(record(group.config)?.dimensionFields);
        if ((original.config != null && !record(original.config)) || !sameJson(before, after)
          || !fields || fields.length !== true || fields.width !== true
          || !["height", "flatLength", "flatWidth"].every(key => typeof fields[key] === "boolean")
          || fields.flatLength !== fields.flatWidth || (!fields.height && fields.flatLength)) throw new Error("尺寸配置只能修改旧尺寸字段，请重新读取核对");
      }
      if (original && isSharedStickerMaterial(original) && (original.isActive !== group.isActive || JSON.stringify(original.values) !== JSON.stringify(group.values))) throw new Error("共享材质请使用高级兼容设置，不能覆盖共享选项");
      for (const value of group.values) if (!original?.values.some(old => old.code === value.code) && value.extraConfig != null && !record(value.extraConfig)) throw new Error("选项配置格式无法无损复制");
    }
    if (baseline.some(group => isSharedStickerMaterial(group) && group.isActive && !draft.some(item => item.code === group.code && item.isActive))) throw new Error("共享材质请使用高级兼容设置");
    const expected: { code: string; config?: unknown; values: { code: string; name: string; sortOrder: number; imageUrl: string | null }[] }[] = [];
    for (const source of draft.filter(group => group.isActive)) {
      let saved = baseline.find(group => group.id === source.id || group.code === source.code);
      if (!saved) {
        saved = await write(() => adapter.createCustomOptionGroup(productId, { code: source.code, name: source.name, inputType: source.inputType,
          isRequired: source.required, unit: source.unit, config: source.config as Record<string, unknown> | null, sortOrder: source.sortOrder }));
      } else {
        if (!saved.isActive) saved = await write(() => adapter.setCustomOptionGroupStatus(productId, saved!.id, "ACTIVE"));
        const changes: { sortOrder?: number; config?: Record<string, unknown> | null } = {};
        if (saved.sortOrder !== source.sortOrder) changes.sortOrder = source.sortOrder;
        if (supportsDimensions(source) && !sameJson(saved.config, source.config)) changes.config = source.config as Record<string, unknown> | null;
        if (Object.keys(changes).length) saved = await write(() => adapter.updateCustomOptionGroup(productId, saved!.id,
          { expectedUpdatedAt: saved!.updatedAt, ...changes }));
      }
      const target = saved;
      const expectedValues: { code: string; name: string; sortOrder: number; imageUrl: string | null }[] = [];
      for (const value of source.values) {
        let old = target.values.find(item => item.id === value.id || item.code === value.code);
        const name = !old || old.name !== value.name ? value.name.normalize("NFKC").trim() : value.name;
        const image = images.find(item => item.groupId === source.id && item.valueId === value.id);
        let imageUrl = value.imageUrl;
        if (image && value.isActive) imageUrl = (await write(() => adapter.uploadCustomizationOptionImage(image.file))).url;
        if (!old) {
          if (!value.isActive) continue; // Disabled public defaults are not activated or copied implicitly.
          await write(() => adapter.createCustomOptionValue(productId, target.id, { code: value.code, name,
            extraConfig: value.extraConfig as Record<string, unknown> | null, imageUrl, sortOrder: value.sortOrder }));
        } else {
          const changes: { name?: string; sortOrder?: number; imageUrl?: string | null } = {};
          if (old.name !== name) changes.name = name;
          if (old.sortOrder !== value.sortOrder) changes.sortOrder = value.sortOrder;
          if (old.imageUrl !== imageUrl) changes.imageUrl = imageUrl;
          if (Object.keys(changes).length) old = await write(() => adapter.updateCustomOptionValue(productId, target.id, old!.id, { expectedUpdatedAt: old!.updatedAt, ...changes }));
          if (old.isActive !== value.isActive) await write(() => adapter.setCustomOptionValueStatus(productId, target.id, old!.id, value.isActive ? "ACTIVE" : "INACTIVE"));
        }
        if (value.isActive) expectedValues.push({ code: value.code, name, sortOrder: value.sortOrder, imageUrl });
      }
      for (const old of target.values) {
        if (old.isActive && !source.values.some(value => value.id === old.id || value.code === old.code)) await write(() => adapter.setCustomOptionValueStatus(productId, target.id, old.id, "INACTIVE"));
      }
      expected.push({ code: source.code, ...(supportsDimensions(source) ? { config: source.config } : {}), values: expectedValues });
    }
    for (const group of baseline) {
      if (group.isActive && !draft.some(item => item.code === group.code && item.isActive)) await write(() => adapter.setCustomOptionGroupStatus(productId, group.id, "INACTIVE"));
    }
    const readback = await adapter.getAdminCustomizationConfig(productId);
    if (readback.optionGroups.filter(group => group.isActive).length !== expected.length) throw new Error("保存后参数组数量不一致，请重新读取核对");
    for (const group of expected) {
      const actual = readback.optionGroups.find(item => item.code === group.code && item.isActive);
      if (!actual || ("config" in group && !sameJson(actual.config, group.config)) || actual.values.filter(value => value.isActive).length !== group.values.length
        || group.values.some(value => !actual.values.some(item => item.isActive && item.code === value.code && item.name === value.name && item.sortOrder === value.sortOrder && item.imageUrl === value.imageUrl))) throw new Error("保存后读回与草稿不一致，请重新读取核对");
    }
    return readback;
  } catch (failure) {
    throw new ParameterSaveError(failure instanceof Error ? failure.message : "参数保存未完成", attempted, completed);
  }
}

export function copyDraft(groups: CustomOptionGroup[]) { return cloneGroups(groups); }
